import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  ChangeMatrixCell,
  FunctionRequirement,
  MatrixAxis,
  MatrixCellStatus,
  MatrixCellUpdateRequest,
  PartTreeNode,
} from '../../../types/drbfm';

interface MatrixCellDrawerProps {
  open: boolean;
  cell: ChangeMatrixCell | null;
  part?: PartTreeNode;
  axis?: MatrixAxis;
  requirements: FunctionRequirement[];
  isLoading: boolean;
  onClose: () => void;
  onSave: (cellId: string, patch: MatrixCellUpdateRequest) => void | Promise<void>;
}

const MatrixCellDrawer: React.FC<MatrixCellDrawerProps> = ({
  open,
  cell,
  part,
  axis,
  requirements,
  isLoading,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();

  const statusOptions: Array<{ value: MatrixCellStatus; label: string }> = [
    { value: 'unchecked', label: t('drbfm.workflow.matrix.statusUnchecked', '未確認') },
    { value: 'none', label: t('drbfm.workflow.matrix.statusNone', '変更なし') },
    { value: 'changed', label: t('drbfm.workflow.matrix.statusChanged', '変更あり') },
    { value: 'important_changed', label: t('drbfm.workflow.matrix.statusImportantChanged', '重要変化あり') },
    { value: 'unknown', label: t('drbfm.workflow.matrix.statusUnknown', '要確認・不明') },
    { value: 'not_applicable', label: t('drbfm.workflow.matrix.statusNotApplicable', '該当なし') },
  ];

  const [status, setStatus] = useState<MatrixCellStatus>('unchecked');
  const [oldValue, setOldValue] = useState('');
  const [newValue, setNewValue] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const [affectedRequirementIds, setAffectedRequirementIds] = useState<string[]>([]);
  const [reviewerNote, setReviewerNote] = useState('');
  const [seededCellId, setSeededCellId] = useState<string | undefined>();

  // Seed the form from the cell during render (React's "a prop changed"
  // pattern), keyed on the stable cell id so an unstable parent object can't
  // loop and re-seeding a still-open cell never clobbers in-progress edits.
  if (cell && cell.id !== seededCellId) {
    setSeededCellId(cell.id);
    setStatus(cell.status);
    setOldValue(cell.oldValue);
    setNewValue(cell.newValue);
    setChangeSummary(cell.changeSummary);
    setAffectedRequirementIds(cell.affectedRequirementIds);
    setReviewerNote(cell.reviewerNote);
  }

  const importantMissingRequirement = (
    status === 'important_changed' && affectedRequirementIds.length === 0
  );

  const fieldsDisabled = isLoading || Boolean(cell?.promotedDesignChangeId);

  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <SheetContent
        side="right"
        className="w-[340px] max-w-[340px] overflow-y-auto sm:w-[480px] sm:max-w-[480px]"
      >
        <div className="flex flex-col gap-5">
          <div>
            <SheetTitle className="text-lg font-semibold text-foreground">{t('drbfm.workflow.matrix.cellJudgment', 'セル判定')}</SheetTitle>
            <p className="text-muted-foreground">
              {part ? `${part.partNo} ${part.partName}` : t('drbfm.workflow.matrix.part', '部品')} × {axis?.axisLabel || t('drbfm.workflow.matrix.inspectionAxis', '点検軸')}
            </p>
          </div>

          {cell?.promotedDesignChangeId && (
            <Alert variant="info">
              <AlertDescription>{t('drbfm.workflow.matrix.promotedCannotEdit', 'このセルは設計変更へ昇格済みのため編集できません。')}</AlertDescription>
            </Alert>
          )}
          {importantMissingRequirement && (
            <Alert variant="warning">
              <AlertDescription>
                {t('drbfm.workflow.matrix.importantNeedsRequirement', '重要変化は、影響する機能要求を1件以上選択してください。')}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="matrix-cell-status">{t('drbfm.workflow.matrix.judgment', '判定')}</Label>
            <Select
              value={status}
              onValueChange={(value) => setStatus(value as MatrixCellStatus)}
              disabled={fieldsDisabled}
            >
              <SelectTrigger id="matrix-cell-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="matrix-cell-old-value">{t('drbfm.workflow.matrix.oldValue', '変更前')}</Label>
            <Textarea
              id="matrix-cell-old-value"
              value={oldValue}
              onChange={(event) => setOldValue(event.target.value)}
              disabled={fieldsDisabled}
              rows={2}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="matrix-cell-new-value">{t('drbfm.workflow.matrix.newValue', '変更後')}</Label>
            <Textarea
              id="matrix-cell-new-value"
              value={newValue}
              onChange={(event) => setNewValue(event.target.value)}
              disabled={fieldsDisabled}
              rows={2}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="matrix-cell-change-summary">{t('drbfm.workflow.matrix.changeSummary', '変化点要約')}</Label>
            <Textarea
              id="matrix-cell-change-summary"
              value={changeSummary}
              onChange={(event) => setChangeSummary(event.target.value)}
              disabled={fieldsDisabled}
              rows={2}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label id="affected-requirements-label">{t('drbfm.workflow.matrix.affectedRequirements', '影響する機能要求')}</Label>
            <div
              role="group"
              aria-labelledby="affected-requirements-label"
              className="flex max-h-48 flex-col gap-2 overflow-y-auto rounded-md border border-input p-3"
            >
              {requirements.map((requirement) => {
                const checked = affectedRequirementIds.includes(requirement.id);
                return (
                  <label
                    key={requirement.id}
                    className="flex items-start gap-2 text-sm text-foreground"
                  >
                    <Checkbox
                      className="mt-0.5"
                      checked={checked}
                      disabled={fieldsDisabled}
                      onCheckedChange={(next) => setAffectedRequirementIds(
                        next === true
                          ? [...affectedRequirementIds, requirement.id]
                          : affectedRequirementIds.filter((id) => id !== requirement.id),
                      )}
                    />
                    <span>{requirement.requirementNo}: {requirement.requirementText}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="matrix-cell-reviewer-note">{t('drbfm.workflow.matrix.reviewerNote', 'レビューメモ')}</Label>
            <Textarea
              id="matrix-cell-reviewer-note"
              value={reviewerNote}
              onChange={(event) => setReviewerNote(event.target.value)}
              disabled={fieldsDisabled}
              rows={2}
            />
          </div>

          {cell?.aiReason && (
            <Alert variant="info">
              <AlertDescription>
                {t('drbfm.workflow.matrix.aiDraft', 'AI下書き')}
                {cell.aiSuggestedStatus ? t('drbfm.workflow.matrix.aiCandidate', '（候補: {{status}}）', { status: cell.aiSuggestedStatus }) : ''}: {cell.aiReason}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-row justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>{t('common.close', '閉じる')}</Button>
            <Button
              disabled={
                !cell
                || isLoading
                || Boolean(cell.promotedDesignChangeId)
                || importantMissingRequirement
              }
              onClick={() => {
                if (!cell) return;
                void onSave(cell.id, {
                  status,
                  oldValue,
                  newValue,
                  changeSummary,
                  affectedRequirementIds,
                  reviewerNote,
                });
              }}
            >
              {t('drbfm.workflow.matrix.saveJudgment', '判定を保存')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MatrixCellDrawer;
